/* Travel Policy Renewal - Multi-step flow wrapper */

import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import useTravelRenewalFlow, {
  TRAVEL_RENEWAL_FLOW,
  TRAVEL_RENEWAL_REVIEW_POLICY,
  TRAVEL_RENEWAL_SELECT_PLAN,
  TRAVEL_RENEWAL_ENHANCE_COVERAGE,
  TRAVEL_RENEWAL_REVIEW_CONFIRM,
  TRAVEL_RENEWAL_SELECTED_POLICY,
} from "../../../hooks/useTravelRenewalFlow";
import useMotorForm from "../../../hooks/useMotorFormData";
import {
  policyView,
  getPolicyViewLoading,
} from "../../../redux/features/policyDetailsSlice";
import { PolicyViewResponse } from "../../../redux/features/motor/getQuoteSlice";
import { getClientSource } from "../../../utils/getClientSource";
import Loader from "../../../components/common/Loader/Loader";

// Step components
import ReviewPolicyPlanPage from "./steps/ReviewPolicyPlanPage";
import SelectNewPlanPage from "./steps/SelectNewPlanPage";
import EnhanceCoveragePage from "./steps/EnhanceCoveragePage";
import ReviewConfirmPage from "./steps/ReviewConfirmPage";

const RenewalTravelPolicy = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get selected policy from location state (passed from GenericSelectPoliciesPage)
  const selectedPolicy = location.state?.selectedPolicy;

  // Save the selected policy to Redux
  const { saveMotorDataInRedux } = useMotorForm(
    TRAVEL_RENEWAL_SELECTED_POLICY,
    () => {}
  );

  // Get policy view data from Redux
  const policyViewResponseDetails = useSelector(PolicyViewResponse);
  const isPolicyViewLoading = useSelector(getPolicyViewLoading);

  // Use the travel renewal flow hook
  const { currentStep, handleFlowChange } = useTravelRenewalFlow();

  // Initialize flow and call policyView API on mount
  useEffect(() => {
    // Initialize the flow at step 0 (Review Policy Plan)
    handleFlowChange(TRAVEL_RENEWAL_FLOW, 0, 0);

    // If we have a selected policy, save it and fetch policy details
    if (selectedPolicy) {
      saveMotorDataInRedux(selectedPolicy);

      const polReq = {
        PolicyNumber: selectedPolicy.PolicyNumber,
        Channel: getClientSource(),
      };

      // Check if we need to fetch policy view (if not already loaded or different policy)
      if (
        !policyViewResponseDetails?.PolicyHeader ||
        policyViewResponseDetails?.PolicyHeader?.PolicyNumber !== selectedPolicy.PolicyNumber
      ) {
        dispatch(policyView(polReq));
      }
    }
  }, []);

  // If no selected policy, redirect back to renewal hub
  useEffect(() => {
    if (!selectedPolicy && !policyViewResponseDetails?.PolicyHeader) {
      navigate("/renew");
    }
  }, [selectedPolicy, policyViewResponseDetails, navigate]);

  // Handle navigation back to renewal hub
  const handleNavigationToRenewalHub = () => {
    navigate("/renew");
  };

  // Show loader while policy view is loading
  if (isPolicyViewLoading) {
    return (
      <div className="mainCarDetailsModal pt-[22px] rtl:max767:pt-0 pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[5%] max767:h-screen ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  // Render appropriate step component based on current step
  const PageRenderer = () => {
    switch (currentStep?.name) {
      case TRAVEL_RENEWAL_REVIEW_POLICY:
        return (
          <ReviewPolicyPlanPage
            handleNavigationToRenewalHub={handleNavigationToRenewalHub}
          />
        );
      case TRAVEL_RENEWAL_SELECT_PLAN:
        return (
          <SelectNewPlanPage
            handleNavigationToRenewalHub={handleNavigationToRenewalHub}
          />
        );
      case TRAVEL_RENEWAL_ENHANCE_COVERAGE:
        return (
          <EnhanceCoveragePage
            handleNavigationToRenewalHub={handleNavigationToRenewalHub}
          />
        );
      case TRAVEL_RENEWAL_REVIEW_CONFIRM:
        return (
          <ReviewConfirmPage
            handleNavigationToRenewalHub={handleNavigationToRenewalHub}
          />
        );
      default:
        return (
          <ReviewPolicyPlanPage
            handleNavigationToRenewalHub={handleNavigationToRenewalHub}
          />
        );
    }
  };

  return (
    <div className="mainCarDetailsModal pt-[22px] rtl:max767:pt-0 pb-[5%] max767:w-full max767:overflow-y-scroll max767:pb-[5%] max767:h-screen ipad:pt-[10%] ipad:h-[93vh] ipad:overflow-y-scroll max767:pt-0">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep?.name || "step"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
        >
          {PageRenderer()}
        </motion.div>
      </AnimatePresence>
      <div id="home-footer-portal" />
    </div>
  );
};

export default RenewalTravelPolicy;
